import {useEffect, useMemo, useRef, useState} from "react";
import Header from "./Header";
import Loading from "./Loading";
import {fetchManuscripts} from "../service/ManuscriptService";
import {ChevronDown, ChevronRight, Check} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {calculateOverallScore} from "../service/Operations";
import {GRID, LIST} from "../constants/Constants";

function Submissions() {
    const [loading, setLoading] = useState(true);
    const [manuscripts, setManuscripts] = useState([]);
    const navigate = useNavigate();

    // TYPE filter (All / Scored / Not Scored)
    const [typeFilter, setTypeFilter] = useState("ALL"); // "ALL" | "SCORED" | "NOT_SCORED"
    const [typeOpen, setTypeOpen] = useState(false);
    const typeRef = useRef(null);

    const [view, setView] = useState(LIST);
    const [smallScreen, setSmallScreen] = useState(!window.innerWidth > 767);

    const TYPE_LABELS = {
        ALL: "All Types",
        COMPLETED: "Completed",
        PENDING: "Pending",
        ERROR: "Failed"
    };

    // DATE filter (same as ScoreHistory)
    const [dateFilter, setDateFilter] = useState("ALL"); // "ALL" | "7D" | "30D" | "CUSTOM"
    const [dateOpen, setDateOpen] = useState(false);
    const dateRef = useRef(null);
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    useEffect(() => {
        const updateRadius = () => {
            const width = window.innerWidth;
            if (width > 767) {
                setSmallScreen(false);
            } else {
                setSmallScreen(true);
            }
        };
        updateRadius();
        window.addEventListener('resize', updateRadius);
        return () => window.removeEventListener('resize', updateRadius);
    }, []);


    const dateLabel = useMemo(() => {
        if (dateFilter === "ALL") return "All Time";
        if (dateFilter === "7D") return "Last 7 days";
        if (dateFilter === "30D") return "Last 30 days";
        if (dateFilter === "CUSTOM") {
            if (customStart && customEnd) {
                const fmt = (s) =>
                    new Date(s + "T00:00:00").toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    });
                return `${fmt(customStart)} – ${fmt(customEnd)}`;
            }
            return "Custom…";
        }
        return "Date Range";
    }, [dateFilter, customStart, customEnd]);

    useEffect(() => {
        setLoading(true);
        fetchManuscripts(true)
            .then((response) => {
                if (response?.manuscripts?.length) {
                    setView(response.view || LIST)
                    setManuscripts(response.manuscripts);
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Close dropdowns on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
            if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const onTypeKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setTypeOpen((o) => !o);
        } else if (e.key === "Escape") {
            setTypeOpen(false);
        }
    };
    const onDateKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDateOpen((o) => !o);
        } else if (e.key === "Escape") {
            setDateOpen(false);
        }
    };

    // Filtered list (type + date)
    const visibleManuscripts = useMemo(() => {
        if (!manuscripts) return [];

        // 1) Type filter
        let list = manuscripts;
        if (typeFilter === "COMPLETED") {
            list = list.filter((m) => m.status === "completed");
        } else if (typeFilter === "PENDING") {
            list = list.filter((m) => m.status === "inProcess" || m.status === "new" || (m.status === "error" && m.attempts < 4));
        } else if (typeFilter === "ERROR") {
            list = list.filter((m) => m.status === "error" && m.attempts > 3);
        }

        // 2) Date filter (use scoredDate if present, otherwise uploadDate)
        if (dateFilter === "ALL") return list;

        const now = Date.now();
        const DAY = 24 * 60 * 60 * 1000;
        let start = null;
        let end = null;

        if (dateFilter === "7D") {
            start = now - 7 * DAY;
            end = now;
        } else if (dateFilter === "30D") {
            start = now - 30 * DAY;
            end = now;
        } else if (dateFilter === "CUSTOM") {
            if (customStart) start = new Date(customStart + "T00:00:00").getTime();
            if (customEnd) {
                const e = new Date(customEnd + "T00:00:00");
                e.setHours(23, 59, 59, 999);
                end = e.getTime();
            }
            if (start !== null && end !== null && start > end) {
                const t = start;
                start = end;
                end = t;
            }
        }

        return list.filter((m) => {
            const t = Number(m.uploadDate);
            if (Number.isNaN(t)) return false;
            if (start !== null && t < start) return false;
            if (end !== null && t > end) return false;
            return true;
        });
    }, [manuscripts, typeFilter, dateFilter, customStart, customEnd]);

    return (
        <div>
            <Header page="submissions"/>
            {loading ? (
                <Loading/>
            ) : (
                <div className="body">
                    <div className="heading">Submissions</div>

                    <div className={"search-bar-wrapper dashboard-row"}>
                        {/* TYPE DROPDOWN */}
                        <div
                            ref={typeRef}
                            className={"submissions-input-half submissions-input-half-first"}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="listbox"
                            aria-expanded={typeOpen}
                            onClick={() => setTypeOpen((o) => !o)}
                            onKeyDown={onTypeKeyDown}
                            style={{position: "relative"}}
                        >
                            <div
                                className="dropdown-trigger"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%"
                                }}
                            >
                                <span className={"dropdown-text"}>{TYPE_LABELS[typeFilter]}</span>
                                <span className="chevron" aria-hidden="true">
                  <ChevronDown className={`chevron-icon ${typeOpen ? "open" : ""}`}/>
                </span>
                            </div>

                            <ul
                                role="listbox"
                                aria-label="Filter by type"
                                className={`dropdown-panel dropdown-panel-regular ${typeOpen ? "open" : ""}`}
                                data-open={typeOpen}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {[
                                    {val: "ALL", label: "All Types"},
                                    {val: "COMPLETED", label: "Completed"},
                                    {val: "PENDING", label: "Pending"},
                                    {val: "ERROR", label: "Failed"},
                                ].map((opt) => (
                                    <li
                                        key={opt.val}
                                        role="option"
                                        aria-selected={typeFilter === opt.val}
                                        onClick={() => {
                                            setTypeFilter(opt.val);
                                            setTypeOpen(false);
                                        }}
                                        style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            background: typeFilter === opt.val ? "#f6f6f6" : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                        className="dropdown-option"
                                    >
                                        <span>{opt.label}</span>
                                        {typeFilter === opt.val && <Check size={16}/>}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* DATE DROPDOWN */}
                        <div
                            ref={dateRef}
                            className={"submissions-input-half"}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="listbox"
                            aria-expanded={dateOpen}
                            onClick={() => setDateOpen((o) => !o)}
                            onKeyDown={onDateKeyDown}
                            style={{position: "relative"}}
                        >
                            <div
                                className="dropdown-trigger"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%"
                                }}
                            >
                                <span className={"dropdown-text"}>{dateLabel}</span>
                                <span className="chevron" aria-hidden="true">
                  <ChevronDown className={`chevron-icon ${dateOpen ? "open" : ""}`}/>
                </span>
                            </div>

                            <ul
                                role="listbox"
                                aria-label="Filter by date range"
                                className={`dropdown-panel dropdown-panel-regular dropdown-panel-date ${dateOpen ? "open" : ""}`}
                                data-open={dateOpen}
                                onClick={(e) => e.stopPropagation()}
                                // style={{ width: 260 }}
                            >
                                {[
                                    {val: "ALL", label: "All Time"},
                                    {val: "7D", label: "Last 7 days"},
                                    {val: "30D", label: "Last 30 days"},
                                    {val: "CUSTOM", label: "Custom…"},
                                ].map((opt) => (
                                    <li
                                        key={opt.val}
                                        role="option"
                                        aria-selected={dateFilter === opt.val}
                                        onClick={() => {
                                            if (opt.val === "CUSTOM") {
                                                setDateFilter("CUSTOM"); // keep panel open for custom inputs
                                            } else {
                                                setDateFilter(opt.val);
                                                setDateOpen(false);
                                            }
                                        }}
                                        style={{
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            background: dateFilter === opt.val ? "#f6f6f6" : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                        className="dropdown-option"
                                    >
                                        <span>{opt.label}</span>
                                        {dateFilter === opt.val && <Check size={16}/>}
                                    </li>
                                ))}

                                {dateFilter === "CUSTOM" && (
                                    <li
                                        className="dropdown-option"
                                        style={{
                                            padding: "12px",
                                            borderTop: "1px solid #eee",
                                            display: "block",
                                            background: "#fff"
                                        }}
                                    >
                                        <div style={{display: "grid", gap: 8}}>
                                            <label style={{fontSize: 12, color: "#555"}}>Start date</label>
                                            <input
                                                type="date"
                                                value={customStart}
                                                onChange={(e) => setCustomStart(e.target.value)}
                                                style={{padding: "8px", border: "1px solid #ddd", borderRadius: 6}}
                                            />
                                            <label style={{fontSize: 12, color: "#555", marginTop: 8}}>End date</label>
                                            <input
                                                type="date"
                                                value={customEnd}
                                                onChange={(e) => setCustomEnd(e.target.value)}
                                                style={{padding: "8px", border: "1px solid #ddd", borderRadius: 6}}
                                            />

                                            <div style={{
                                                display: "flex",
                                                gap: 8,
                                                marginTop: 12,
                                                justifyContent: "flex-end"
                                            }}>
                                                <div style={{margin: "auto"}}>
                                                    <button
                                                        className={"clear-button"}
                                                        type="button"
                                                        onClick={() => {
                                                            setCustomStart("");
                                                            setCustomEnd("");
                                                        }}
                                                        style={{
                                                            padding: "8px 10px",
                                                            border: "1px solid #ddd",
                                                            background: "#fff",
                                                            borderRadius: 6,
                                                            cursor: "pointer",
                                                            marginRight: "10px",
                                                        }}
                                                    >
                                                        Clear
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={!customStart && !customEnd}
                                                        onClick={() => setDateOpen(false)}
                                                        style={{
                                                            padding: "8px 10px",
                                                            border: "1px solid #0b5cff",
                                                            background: !customStart || !customEnd ? "#e6ecff" : "#0b5cff",
                                                            color: !customStart || !customEnd ? "#6b7aff" : "#fff",
                                                            borderRadius: 6,
                                                            cursor: !customStart && !customEnd ? "not-allowed" : "pointer",
                                                        }}
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>

                    {/* LIST */}
                    {visibleManuscripts && visibleManuscripts.length > 0 ? (
                        <div className="submissions-list">
                            {[...visibleManuscripts]
                                .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate)) // 🔽 newest first
                                .map((m) => {
                                    const title =
                                        (m.title && m.title.trim()) ||
                                        (m.name ? m.name.replace(/\.[^/.]+$/, "") : "Untitled");
                                    const authors =
                                        m.authors && m.authors.trim() ? m.authors : "Unknown authors";
                                    const overallScore = calculateOverallScore(m);

                                    return (
                                        <div
                                            onClick={() => navigate("/submission?id=" + m.id)}
                                            style={{cursor: 'pointer', position: 'relative'}}
                                            key={m.id}
                                            className={!smallScreen && view === GRID ? "fade-in upload score-history-grid-item" : "submissions-card fade-in"}
                                        >
                                            <div className="submissions-title" title={title}>
                                                {title}
                                            </div>

                                            {typeof m.noveltyScore === "number" ? (
                                                <div className="novelty-pill">{overallScore}</div>
                                            ) : (
                                                <div className="novelty-pill">?</div>
                                            )}

                                            <div className="submissions-authors" title={authors}>
                                                {authors}
                                            </div>

                                            {m.status === "inProcess" || m.status === "new" || (m.status === "error" && m.attempts < 4) ? (
                                                <div className="chip chip-pending">Pending Review</div>
                                            ) : m.status === "error" ? (
                                                <div className="chip chip-pending">Failed</div>
                                            ) : overallScore >= 65 ? (
                                                <div className="chip chip-accept">Suggested Acceptance</div>
                                            ) : (
                                                <div className="chip chip-reject">Suggested Rejection</div>
                                            )}
                                            {view === LIST &&
                                                <ChevronRight className="submissions-chevron"/>
                                            }
                                        </div>
                                    );
                                })}
                        </div>
                    ) : (
                        <div>No manuscripts found</div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Submissions;
